import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  formatAmountWithCommas,
  isScopeAnyGlobalType
} from '../../shared/UtilFunctions';
import {
  NonStrategicTooltipContent,
  StrategicTooltipContent
} from '../../shared/SupplierStatusTooltip';
import StatusEdit from './StatusEdit';

function getSpendMap(mapJson) {
  let map = null;

  if (mapJson) {
    const spendJson = JSON.parse(JSON.stringify(mapJson));

    map = new Map(Object.entries(spendJson));
  }

  return map;
}

function processUnitGroupSpend(spendMap, splrNumber, unitGroupSpendMap) {
  if (spendMap && unitGroupSpendMap) {
    for (const item of unitGroupSpendMap.entries()) {
      // Unit Group As Key
      addSpend(spendMap, item[0], item[1].spend);
      // Unit Group & Splr Number as key
      addSpend(spendMap, getMapKey(item[0], splrNumber), item[1].spend);
      // Process Unit Spend
      processUnitSpend(spendMap, splrNumber, getSpendMap(item[1].spendByUnits));
    }
  }
}

function addSpend(spendMap, key, spendAmt) {
  let entry = spendMap.get(key);

  spendMap.set(key, entry ? entry + spendAmt : spendAmt);
}

function processUnitSpend(spendMap, splrNumber, unitSpendMap) {
  if (spendMap && unitSpendMap) {
    for (const item of unitSpendMap.entries()) {
      // Unit As Key
      addSpend(spendMap, item[0], item[1]);
      // Unit  & Splr Number as key
      addSpend(spendMap, getMapKey(item[0], splrNumber), item[1]);
    }
  }
}

export function findSpend(map, code) {
  let value = map?.get(code);
  return !value ? 0 : value;
}
export function getMapKey(precedingKey, splrNumber) {
  return precedingKey + '-' + splrNumber;
}
export function prepareSpendMaps(suppliers) {
  let masterInvoiceSpendMap = new Map();
  let masterForecastSpendMap = new Map();

  suppliers?.forEach((splr) => {
    // Invoice
    processUnitGroupSpend(
      masterInvoiceSpendMap,
      splr.supplierNumber,
      getSpendMap(splr.mapInvoiceSpend)
    );
    // Forecast
    processUnitGroupSpend(
      masterForecastSpendMap,
      splr.supplierNumber,
      getSpendMap(splr.mapForecastSpend)
    );
  });

  return {
    masterInvoiceSpendMap: masterInvoiceSpendMap,
    masterForecastSpendMap: masterForecastSpendMap
  };
}

function mapToList(masterUnitList, unitCodeMap) {
  let listOfUnits = [];

  masterUnitList.forEach((unit) => {
    if (unitCodeMap.has(unit.code)) {
      listOfUnits.push(unit);
    }
  });
  return listOfUnits;
}
export function getSelectedScopeStructured(
  masterScopeGroups,
  supplierScopeOverview,
  masterData
) {
  const scopeLevel = supplierScopeOverview?.level;
  if (isScopeAnyGlobalType(scopeLevel)) {
    return masterData?.globalLevelMap?.[scopeLevel] || masterScopeGroups;
  } else if (masterScopeGroups) {
    // unit group fully selected
    let partialGroupList =
      supplierScopeOverview?.unitGroups?.length > 0
        ? supplierScopeOverview?.unitGroups.map((unitGroupCode) => {
            return { code: unitGroupCode, hollow: true };
          })
        : [];

    // unit group partial for unit groups
    if (supplierScopeOverview?.units?.length > 0 && masterData?.unitsMap) {
      const unitMapJson = JSON.parse(JSON.stringify(masterData.unitsMap));
      const masterUnitMap = new Map(Object.entries(unitMapJson));
      const partialSelectedMap = new Map();

      supplierScopeOverview.units.forEach((unitCode) => {
        const parentUnitGroupObject = masterUnitMap.get(unitCode);
        if (!parentUnitGroupObject?.parent) {
          return;
        }

        let mapObj = partialSelectedMap.get(parentUnitGroupObject.parent);

        if (!mapObj) {
          mapObj = new Map();
          partialSelectedMap.set(parentUnitGroupObject.parent, mapObj);
        }
        mapObj.set(unitCode, unitCode);
      });
      const iterator1 = partialSelectedMap[Symbol.iterator]();

      for (const item of iterator1) {
        let [groupCode, unitCodeMap] = item;
        const unitGroupCodeObj = masterScopeGroups.find(
          (unitGroup) => unitGroup.code === groupCode
        );
        if (unitGroupCodeObj) {
          const partialUnitGroupObj = {
            ...unitGroupCodeObj,
            notFullySelected: true,
            units: mapToList(unitGroupCodeObj.units, unitCodeMap)
          };

          partialGroupList.push(partialUnitGroupObj);
        }
      }
    }

    let unitGroupList = [];

    if (partialGroupList.length > 0) {
      masterScopeGroups.forEach((masterUnitGroupObj) => {
        const unitGroupObj = partialGroupList.find(
          (unitGroup) => unitGroup.code === masterUnitGroupObj.code
        );

        unitGroupObj &&
          unitGroupList.push(
            unitGroupObj.hollow ? masterUnitGroupObj : unitGroupObj
          );
      });
    }
    return unitGroupList;
  }
}

function getSupplierStatus(splr) {
  return splr.statusDesc || splr.status;
}
export function checkSanity(statusScope, parentStatusScope) {
  if (parentStatusScope && !statusScope)
    throw new Error('Error - Parent Scope present but self scope not...! ');
}

export function findStatus(splr, statusScope, parentStatus, parentStatusScope) {
  checkSanity(statusScope, parentStatusScope);

  let status;
  let map = splr.statusMap;

  if (map) {
    if (parentStatusScope) {
      const unitGroupStatusObj = map.get(parentStatusScope);

      if (unitGroupStatusObj) {
        const childMap = unitGroupStatusObj.statusMap;

        if (childMap) status = childMap.get(statusScope);
      }
    } else if (statusScope) status = map.get(statusScope)?.status;
    else status = map.get(defaultStatusKey);
  }

  status ??= statusScope ? parentStatus : getSupplierStatus(splr);

  return status;
}
export function findStatusWithFallback(splr, unitGroupCode, unitCode) {
  let map = splr.statusMap;
  let status;

  if (map) {
    // Lookup By Unit Group
    const unitGroupStatusObj = map.get(unitGroupCode);

    if (unitGroupStatusObj) {
      // Lookup By Unit
      status = unitGroupStatusObj.statusMap?.get(unitCode);
      status ??= unitGroupStatusObj.status;
    }
  }

  return status || getSupplierStatus(splr);
}
export function processRowUpdateForStatusOnly(
  updatedRow,
  updateSupplierStatus
) {
  updateSupplierStatus(
    updatedRow.number,
    updatedRow.status,
    updatedRow.statusScope,
    updatedRow.parentStatusScope
  );

  return updatedRow;
}

export const handleDoubleClickEvent = (params, event) => {
  if (
    event.target &&
    (params.field === 'status' || params.field === 'comments')
  ) {
    const doubleClickEvent = new MouseEvent('dblclick', {
      view: window,
      bubbles: true,
      cancelable: true
    });
    event.target.dispatchEvent(doubleClickEvent);
  }
};

export const defaultStatus = '---';
export const defaultSupplierNumber = '0000';
export const defaultStatusKey = defaultStatus;
export const SUPPLIER_NUMBER_COLUMN_WIDTH = 140;
export const SUPPLIER_NAME_COLUMN_WIDTH = 300;
export const SUPPLIER_STATUS_COLUMN_WIDTH = 200;
export const R12_SPEND_COLUMN_WIDTH = 140;
export const R12_FORECAST_COLUMN_WIDTH = 150;

export const SPEND_COLUMNS = [
  {
    field: 'r12Spend',
    headerName: 'R12 Spend',
    width: R12_SPEND_COLUMN_WIDTH,
    minWidth: R12_SPEND_COLUMN_WIDTH,
    flex: 0,
    align: 'right',
    type: 'number',
    valueFormatter: (value) => value && formatAmountWithCommas(value, true)
  },
  {
    field: 'r12Forecast',
    headerName: 'R12 Forecast',
    width: R12_FORECAST_COLUMN_WIDTH,
    minWidth: R12_FORECAST_COLUMN_WIDTH,
    flex: 0,
    align: 'right',
    type: 'number',
    valueFormatter: (value) => value && formatAmountWithCommas(value, true)
  }
];

export function getStatusColumnObject(isEditable, strategicOrNonStrategic) {
  return {
    field: 'status',
    headerName: 'Status',
    width: SUPPLIER_STATUS_COLUMN_WIDTH,
    minWidth: SUPPLIER_STATUS_COLUMN_WIDTH,
    flex: 0,
    editable: isEditable,
    renderHeader: () => (
      <>
        <Typography variant="labelMedium" color="primary">
          Status
        </Typography>
        <Tooltip
          title={
            strategicOrNonStrategic === 'Strategic' ? (
              <StrategicTooltipContent />
            ) : (
              <NonStrategicTooltipContent />
            )
          }
        >
          <fuel-icon name="info_outlined" />
        </Tooltip>
      </>
    ),
    renderEditCell: (params) => <StatusEdit {...params} />,
    renderCell: (params) =>
      paintStatusWithMore(
        params.row.statusMapSize,
        params.value,
        false,
        isEditable
      )
  };
}

export function StatusBadge(props) {
  return (
    <Chip
      color="informational"
      variant="badge"
      label={props.statusMsg}
      size="small"
      sx={props?.bold ? { fontWeight: 'bold' } : undefined}
    />
  );
}

export function paintStatusWithMore(
  childMapSize,
  value,
  notFullySelected,
  isEditable
) {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flex: 1,
          minWidth: 0
        }}
      >
        {value}
        {childMapSize > 0 && (
          <StatusBadge statusMsg={`+ ${childMapSize} Exceptions`} bold={true} />
        )}
      </Box>
      {isEditable && !notFullySelected && (
        <Box
          component="span"
          sx={(theme) => ({
            color: theme.fuel6Tokens.JddcColorIconSecondary,
            flexShrink: 0,
            display: 'inline-flex'
          })}
        >
          <fuel-icon name="keyboard_arrow_down" />
        </Box>
      )}
    </Box>
  );
}
